class LegalToc extends HTMLElement {
  connectedCallback() {
    this.body = this.closest('.legal-page__layout')?.querySelector('.legal-page__body');
    if (!this.body) {
      this.hidden = true;
      return;
    }

    this.headings = [...this.body.querySelectorAll('h2, h3')];
    if (this.headings.length < 3) {
      this.hidden = true;
      return;
    }

    this.assignHeadingIds();
    this.buildNav();
    this.observeHeadings();
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.removeEventListener('click', this.handleClick);
  }

  assignHeadingIds() {
    const usedIds = new Set();

    for (const heading of this.headings) {
      if (!heading.id) {
        heading.id = this.uniqueSlug(heading.textContent, usedIds);
      }
      usedIds.add(heading.id);
    }
  }

  uniqueSlug(text, usedIds) {
    const base =
      text
        .toLowerCase()
        .replace(/[^\p{L}\p{N}\s-]/gu, '')
        .trim()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-') || 'section';

    let slug = base;
    let counter = 2;
    while (usedIds.has(slug)) {
      slug = `${base}-${counter}`;
      counter += 1;
    }
    return slug;
  }

  buildNav() {
    const details = document.createElement('details');
    details.className = 'legal-page__toc-details';
    details.open = !window.matchMedia('(max-width: 999px)').matches;

    const summary = document.createElement('summary');
    summary.className = 'legal-page__toc-title';
    summary.textContent = this.dataset.tocTitle || 'On this page';
    details.appendChild(summary);

    const list = document.createElement('ul');
    list.className = 'legal-page__toc-list';

    for (const heading of this.headings) {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'legal-page__toc-link';
      if (heading.tagName === 'H3') {
        link.classList.add('legal-page__toc-link--h3');
      }
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent;
      item.appendChild(link);
      list.appendChild(item);
    }

    details.appendChild(list);
    this.appendChild(details);

    this.links = new Map();
    for (const link of list.querySelectorAll('.legal-page__toc-link')) {
      this.links.set(link.getAttribute('href').slice(1), link);
    }
  }

  observeHeadings() {
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.setActive(entry.target.id);
          }
        }
      },
      { rootMargin: '-10% 0px -80% 0px', threshold: 0 }
    );

    for (const heading of this.headings) {
      this.observer.observe(heading);
    }
  }

  setActive(id) {
    for (const [linkId, link] of this.links) {
      const active = linkId === id;
      link.classList.toggle('is-active', active);
      if (active) this.revealActive(link);
    }
  }

  revealActive(link) {
    const list = link.closest('.legal-page__toc-list');
    if (!list || list.scrollHeight <= list.clientHeight) return;

    const linkRect = link.getBoundingClientRect();
    const listRect = list.getBoundingClientRect();
    const pad = 8;

    if (linkRect.top < listRect.top + pad) {
      list.scrollBy({ top: linkRect.top - listRect.top - pad, behavior: 'smooth' });
    } else if (linkRect.bottom > listRect.bottom - pad) {
      list.scrollBy({ top: linkRect.bottom - listRect.bottom + pad, behavior: 'smooth' });
    }
  }

  handleClick = (event) => {
    const link = event.target.closest('.legal-page__toc-link');
    if (!link) return;

    const id = link.getAttribute('href').slice(1);
    const target = document.getElementById(id);
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth' });
    history.replaceState(null, '', `#${id}`);
  };
}

if (!customElements.get('legal-toc')) {
  customElements.define('legal-toc', LegalToc);
}
